import { Button, MagellanPopup } from 'components/atoms';
import { Form, Formik, FormikConfig, FormikHelpers, FormikProps } from 'formik';
import { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PiBroom } from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

export interface IPopupFormProps<T extends {}>
  extends Omit<FormikConfig<T>, 'children' | 'onSubmit'> {
  trigger?: JSX.Element;
  header: ReactNode | ReactNode[];
  children:
    | ReactNode
    | ReactNode[]
    | ((props: FormikProps<T>) => ReactNode | ReactNode[]);
  onSubmit: (
    values: T,
    formikHelpers: FormikHelpers<T>,
    actions: PopupActions | null
  ) => void | Promise<any>;
}

const PopupForm = <T extends {}>({
  trigger,
  header,
  children,
  onSubmit,
  ...props
}: IPopupFormProps<T>) => {
  const { t } = useTranslation();

  const popupRef = useRef<PopupActions>(null);

  return (
    <Popup ref={popupRef} modal nested trigger={trigger}>
      <Formik
        {...props}
        onSubmit={(values, helpers) =>
          onSubmit(values, helpers, popupRef.current)
        }>
        {formikProps => (
          <Form className='w-full h-full'>
            <MagellanPopup>
              <MagellanPopup.Header className='justify-center w-80 mb-5'>
                {header}
              </MagellanPopup.Header>
              {typeof children === 'function'
                ? children(formikProps)
                : children}
              <MagellanPopup.BottomButtons>
                <Button
                  isClear
                  onlyIcon
                  onClick={() => formikProps.setValues(props.initialValues)}>
                  <Button.Icon icon={<PiBroom />} />
                </Button>
                <Button type='submit' variant={BUTTON_VARIANT.submit}>
                  <Button.Text text={t('button.add')} />
                </Button>
              </MagellanPopup.BottomButtons>
            </MagellanPopup>
          </Form>
        )}
      </Formik>
    </Popup>
  );
};

export default PopupForm;
